import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { SERVICES } from "@/lib/site";
import { AREA_PAGES } from "@/content/areas";
import { Seo } from "@/components/Seo";
import { breadcrumbJsonLd } from "@/components/Breadcrumb";
import { PageHero } from "@/components/PageHero";
import { ImageSlot } from "@/components/ImageSlot";
import { BodySections } from "@/components/service/BodySections";
import { FaqBlock, faqJsonLd } from "@/components/FaqBlock";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/home/CtaBand";

export default function AreaPage({ area }) {
  const breadcrumb = [
    { label: "Home", to: "/" },
    { label: "Areas", to: "/areas" },
    { label: area.name, to: area.path },
  ];
  const jsonLd = [breadcrumbJsonLd(breadcrumb), faqJsonLd(area.faqs)];
  const neighbourLinks = area.neighbours.map((n) => ({ name: n, page: AREA_PAGES.find((p) => p.name === n) }));

  return (
    <div data-testid={`area-page-${area.id}`}>
      <Seo title={area.title} description={area.metaDescription} jsonLd={jsonLd} />
      <PageHero eyebrow="Areas We Cover" h1={area.h1} intro={area.intro} breadcrumb={breadcrumb} />

      <section className="bg-white pb-16 md:pb-20">
        <div className="container-site">
          <div className="grid gap-6 md:grid-cols-2 xl:gap-8">
            {area.photos.map((photo, i) => (
              <Reveal key={photo.id} delay={i * 60}>
                <ImageSlot label={photo.id} note={photo.note} ratio="4:3" alt={photo.alt} className="rounded-card" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BodySections sections={area.sections} />

      <section data-testid="area-services" aria-labelledby="area-services-heading" className="section section-alt">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="area-services-heading">Our Services in {area.name}</h2>
            <p className="sub-line mt-4 text-body">Everything we do, available to homeowners across {area.name}.</p>
          </Reveal>
          <ul className="mt-10 grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal as="li" key={s.id} delay={i * 60}>
                <Link
                  to={s.to}
                  data-testid={`area-service-link-${s.id}`}
                  className="group flex items-center justify-between gap-3 border-b border-line py-3 text-base font-medium text-navy transition-colors duration-200 hover:text-brand"
                >
                  {s.title} in {area.name}
                  <ArrowRight size={18} aria-hidden="true" className="shrink-0 text-brand transition-transform duration-200 ease-out group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </ul>

          <Reveal className="mt-10 max-w-[720px]">
            <h3 className="flex items-center gap-2 text-xl">
              <MapPin size={20} className="text-brand" aria-hidden="true" />
              Near {area.name}
            </h3>
            <p className="mt-3 text-base text-body">
              We also cover the areas around {area.name}, including{" "}
              {neighbourLinks.map((n, i) => (
                <span key={n.name}>
                  {n.page ? (
                    <Link to={n.page.path} data-testid={`neighbour-link-${n.page.id}`} className="font-medium text-brand underline-offset-2 transition-colors hover:text-brand-dark hover:underline">
                      {n.name}
                    </Link>
                  ) : (
                    n.name
                  )}
                  {i < neighbourLinks.length - 1 ? (i === neighbourLinks.length - 2 ? " and " : ", ") : "."}
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </section>

      <FaqBlock heading={`${area.name} — Your Questions Answered`} faqs={area.faqs} alt={false} />
      <CtaBand />
    </div>
  );
}
